import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useTutorial, TutorialStep } from 'contexts/TutorialContext';

const TutorialCompleteModal: React.FC = () => {
  const { t } = useTranslation();
  const { currentStep, isTutorialActive, completeTutorial } = useTutorial();

  useEffect(() => {
    if (isTutorialActive && currentStep === TutorialStep.COMPLETE) {
      // Auto-close after 5 seconds
      const timer = setTimeout(() => {
        completeTutorial();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [currentStep, isTutorialActive, completeTutorial]);

  if (!isTutorialActive || currentStep !== TutorialStep.COMPLETE) {
    return null;
  }

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        animation: 'fadeIn 0.3s ease-in'
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          textAlign: 'center',
          color: 'white',
          animation: 'scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
        }}
      >
        {/* Success Icon */}
        <div
          style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 30px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'bounce 1s ease-in-out'
          }}
        >
          <i
            className="bi bi-check-circle-fill"
            style={{
              fontSize: '60px',
              color: '#4ade80'
            }}
          ></i>
        </div>

        {/* Congrats Title */}
        <h1
          style={{
            fontSize: '32px',
            fontWeight: '700',
            marginBottom: '15px',
            background: 'linear-gradient(to right, #fff, #e0e7ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          {t('modals.tutorial.congratulations')}
        </h1>

        {/* Success Message */}
        <p
          style={{
            fontSize: '18px',
            marginBottom: '25px',
            lineHeight: '1.6',
            opacity: 0.95
          }}
        >
          {t('modals.tutorial.successMessage')}
        </p>

        {/* Encouragement Message */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '30px',
            backdropFilter: 'blur(10px)'
          }}
        >
          <p
            style={{
              fontSize: '15px',
              marginBottom: '10px',
              fontWeight: '500'
            }}
          >
            {t('modals.tutorial.whatsNext')}
          </p>
          <p
            style={{
              fontSize: '14px',
              opacity: 0.9,
              lineHeight: '1.5'
            }}
          >
            {t('modals.tutorial.nextSteps')}
          </p>
        </div>

        {/* Tips */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '25px'
          }}
        >
          <p
            style={{
              fontSize: '13px',
              opacity: 0.85,
              margin: 0
            }}
          >
            {t('modals.tutorial.proTip')}
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={completeTutorial}
          style={{
            background: 'white',
            color: '#667eea',
            border: 'none',
            padding: '12px 30px',
            borderRadius: '25px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
          }}
        >
          {t('modals.tutorial.gotIt')}
        </button>

        {/* Auto-close indicator */}
        <p
          style={{
            marginTop: '20px',
            fontSize: '12px',
            opacity: 0.7
          }}
        >
          {t('modals.tutorial.autoClose')}
        </p>
      </div>

      {/* Add keyframe animations via style tag */}
      <style>{`
        @keyframes scaleIn {
          0% {
            transform: scale(0.5);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TutorialCompleteModal;
